import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Globe, Sparkles, Code2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useLanguage } from '../contexts/LanguageContext';
export function LanguageSelect() {
  const {
    setLanguage,
    setHasSelectedLanguage
  } = useLanguage();
  const handleSelect = (lang: 'en' | 'kn') => {
    setLanguage(lang);
    setHasSelectedLanguage(true);
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden" data-id="element-851">
      {/* Background image */}
      <div className="absolute inset-0 bg-cover bg-center" style={{
      backgroundImage: 'url(https://images.unsplash.com/photo-1497633762265-9d179a990aa6?w=1920&q=80)'
    }} data-id="element-852" />
      {/* Gradient overlay with Rwanda colors */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/85 via-blue-800/80 to-green-900/85" data-id="element-853" />

      {/* Floating particles */}
      {[...Array(6)].map((_, i) => <motion.div key={i} className="absolute h-2 w-2 rounded-full bg-white/20" initial={{
      x: Math.random() * 400 - 200,
      y: Math.random() * 400 - 200,
      opacity: 0
    }} animate={{
      y: [0, -30, 0],
      opacity: [0, 0.6, 0]
    }} transition={{
      duration: 3 + i * 0.5,
      repeat: Infinity,
      delay: i * 0.8
    }} style={{
      left: `${15 + i * 14}%`,
      top: `${20 + i % 3 * 25}%`
    }} data-id="element-854" />)}

      <motion.div initial={{
      scale: 0.8,
      opacity: 0,
      y: 20
    }} animate={{
      scale: 1,
      opacity: 1,
      y: 0
    }} transition={{
      type: 'spring',
      duration: 0.7
    }} className="relative z-10 w-full max-w-md mx-4" data-id="element-855">
        <div className="bg-background/95 backdrop-blur-xl rounded-2xl shadow-2xl p-8 text-center border border-white/10" data-id="element-856">
          {/* Animated Logo */}
          <motion.div initial={{
          y: -30,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.2,
          type: 'spring'
        }} className="flex justify-center mb-4" data-id="element-857">
            <motion.div animate={{
            rotate: [0, 5, -5, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity
          }} className="relative" data-id="element-858">
              <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 flex items-center justify-center shadow-lg shadow-blue-600/30" data-id="element-859">
                <GraduationCap className="h-10 w-10 text-white" data-id="element-860" />
              </div>
              <motion.div animate={{
              scale: [1, 1.2, 1]
            }} transition={{
              duration: 2,
              repeat: Infinity
            }} className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-yellow-400 flex items-center justify-center" data-id="element-861">
                <Sparkles className="h-3 w-3 text-yellow-800" data-id="element-862" />
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Title */}
          <motion.div initial={{
          y: -10,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.3
        }} data-id="element-863">
            <h1 className="font-heading text-2xl font-bold mb-1" data-id="element-864">
              Theo EduMarket Rwanda
            </h1>
            <div className="flex items-center justify-center gap-2 text-muted-foreground mb-2" data-id="element-865">
              <Globe className="h-4 w-4" data-id="element-866" />
              <span className="text-sm" data-id="element-867">
                Choose Your Language / Hitamo Ururimi
              </span>
            </div>
          </motion.div>

          {/* Rwanda flag stripe */}
          <motion.div initial={{
          scaleX: 0
        }} animate={{
          scaleX: 1
        }} transition={{
          delay: 0.4,
          duration: 0.5
        }} className="flex h-1.5 rounded-full overflow-hidden mb-8" data-id="element-868">
            <div className="flex-1 bg-blue-600" data-id="element-869" />
            <div className="flex-1 bg-yellow-500" data-id="element-870" />
            <div className="flex-1 bg-green-600" data-id="element-871" />
          </motion.div>

          {/* Language Buttons */}
          <div className="space-y-3" data-id="element-872">
            <motion.div initial={{
            x: -40,
            opacity: 0
          }} animate={{
            x: 0,
            opacity: 1
          }} transition={{
            delay: 0.5,
            type: 'spring'
          }} data-id="element-873">
              <Button onClick={() => handleSelect('en')} className="w-full h-14 text-lg font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-xl gap-3 shadow-lg shadow-blue-600/25" data-id="element-874">
                <motion.span animate={{
                rotate: [0, 10, -10, 0]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                delay: 1
              }} className="text-2xl" data-id="element-875">
                  🇬🇧
                </motion.span>
                English
              </Button>
            </motion.div>

            <motion.div initial={{
            x: 40,
            opacity: 0
          }} animate={{
            x: 0,
            opacity: 1
          }} transition={{
            delay: 0.6,
            type: 'spring'
          }} data-id="element-876">
              <Button onClick={() => handleSelect('kn')} variant="outline" className="w-full h-14 text-lg font-semibold rounded-xl gap-3 border-2 border-green-600 text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 shadow-lg shadow-green-600/10" data-id="element-877">
                <motion.span animate={{
                rotate: [0, -10, 10, 0]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                delay: 1.5
              }} className="text-2xl" data-id="element-878">
                  🇷🇼
                </motion.span>
                Ikinyarwanda
              </Button>
            </motion.div>
          </div>

          {/* Bottom info */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.8
        }} className="mt-6 space-y-2" data-id="element-879">
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground" data-id="element-880">
              <GraduationCap className="h-3 w-3" data-id="element-881" />
              <span data-id="element-882">Past Papers • Books • Study Materials</span>
            </div>
            <p className="text-[10px] text-muted-foreground/60" data-id="element-883">
              Your Digital Educational Marketplace • Isoko ry'ikoranabuhanga
              ry'uburezi
            </p>
            <div className="flex items-center justify-center gap-1 text-[9px] text-muted-foreground/40 mt-1" data-id="element-884">
              <Code2 className="h-2.5 w-2.5" data-id="element-885" />
              <span data-id="element-886">Developed by Bazatsinda Paulin</span>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>;
}